import { useEffect, useRef } from 'react';

import type { FieldValues, SubmitHandler, UseFormHandleSubmit } from 'react-hook-form';

import { useRegisterSave } from './useSectionSavers';

// Registers a section's save with the leave-guard; rejects when invalid so navigation stays blocked.
export const useRegisterSectionSave = <T extends FieldValues>(
  formId: string,
  isDirty: boolean,
  handleSubmit: UseFormHandleSubmit<T>,
  onSubmit: SubmitHandler<T>
) => {
  const registerSave = useRegisterSave();

  const saveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  saveRef.current = async () => {
    if (!isDirty) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      void handleSubmit(
        async (data, event) => {
          try {
            await onSubmit(data, event);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        () => {
          const error = new Error(`Form "${formId}" is invalid`);
          error.name = 'FormValidationError';
          reject(error);
        }
      )();
    });
  };

  useEffect(() => {
    registerSave(formId, () => saveRef.current());
    return () => registerSave(formId, null);
  }, [formId, registerSave]);
};
